import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ASSET_TYPES, type Asset } from "@/contracts";

import { assetsQuery, siteOptionsQuery, type AssetFilters } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { useListFilters } from "@/lib/use-list-filters";
import {
  EM_DASH,
  formatCurrentRange,
  formatDistanceRange,
  formatHumidityRange,
  formatTemperatureRange,
} from "@/lib/format";
import { AssetDeleteDialog } from "./asset-delete-dialog";
import { AssetFormDialog } from "./asset-form-dialog";
import { ASSET_TYPE_LABELS, formatOccupancy } from "./asset-labels";
import { monitors } from "./asset-monitoring";

export function AssetsPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const canCreate = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Asset | null>(null);
  const [deleting, setDeleting] = useState<Asset | null>(null);

  const filters: AssetFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("siteId") ? { siteId: filterValue("siteId") } : {}),
    ...(filterValue("type") ? { type: filterValue("type") } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    page,
  };

  const assets = useQuery(assetsQuery(client, filters));
  const sites = useQuery(siteOptionsQuery(client, filterValue("organizationId") || undefined));

  const columnCount = 10 + (isSuperAdmin ? 1 : 0) + (canCreate ? 1 : 0);

  return (
    <>
      <PageHeader
        title={t("assetsTitle")}
        description={t("assetsDescription")}
        action={canCreate ? <Button onClick={() => setCreating(true)}>{t("addAsset")}</Button> : null}
      />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filterValue("search")}
              placeholder={t("searchAssets")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={tCommon("type")}
              value={filterValue("type")}
              onChange={(value) => setFilter("type", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...ASSET_TYPES.map((type) => ({
                  value: type,
                  label: ASSET_TYPE_LABELS[type],
                })),
              ]}
            />

            <SelectField
              label={tCommon("site")}
              value={filterValue("siteId")}
              onChange={(value) => setFilter("siteId", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...(sites.data?.data ?? []).map((site) => ({
                  value: site.id,
                  label: site.name,
                })),
              ]}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {assets.isPending ? <LoadingBlock label={t("loadingAssets")} /> : null}

      {assets.isError ? (
        <ErrorBlock error={assets.error} onRetry={() => void assets.refetch()} />
      ) : null}

      {assets.data ? (
        <Card>
          <DataTable caption="Assets">
            <thead>
              <tr>
                {isSuperAdmin ? <Th>{tCommon("organization")}</Th> : null}
                <Th>{tCommon("name")}</Th>
                <Th>{tCommon("type")}</Th>
                <Th>{tCommon("site")}</Th>
                <Th>{t("colTemperatureRange")}</Th>
                <Th>{t("colHumidityRange")}</Th>
                <Th>{t("colDistanceRange")}</Th>
                <Th>{t("colCurrentRange")}</Th>
                <Th>{t("colExpectedOccupancy")}</Th>
                <Th numeric>{t("colDevices")}</Th>
                <Th>{t("colRecord")}</Th>
                {canCreate ? <Th>{tCommon("actions")}</Th> : null}
              </tr>
            </thead>

            <tbody>
              {assets.data.data.length === 0 ? (
                <EmptyRow colSpan={columnCount}>{t("emptyAssets")}</EmptyRow>
              ) : null}

              {assets.data.data.map((asset) => (
                <tr key={asset.id}>
                  {isSuperAdmin ? <Td>{asset.organizationName}</Td> : null}
                  <Td className="font-medium">{asset.name}</Td>
                  <Td>{ASSET_TYPE_LABELS[asset.type]}</Td>
                  <Td>{asset.siteName}</Td>
                  <Td className="tabular-nums">
                    {formatTemperatureRange(asset.targetTempMinC, asset.targetTempMaxC)}
                  </Td>
                  <Td className="tabular-nums">
                    {formatHumidityRange(asset.targetHumidityMin, asset.targetHumidityMax)}
                  </Td>
                  <Td className="tabular-nums">
                    {formatDistanceRange(asset.targetDistanceMinMm, asset.targetDistanceMaxMm)}
                  </Td>
                  <Td className="tabular-nums">
                    {formatCurrentRange(asset.targetCurrentMinA, asset.targetCurrentMaxA)}
                  </Td>
                  <Td>{formatOccupancy(asset)}</Td>
                  <Td numeric>
                    {asset.deviceCount > 0 ? (
                      <Link
                        to={`/devices?search=${encodeURIComponent(asset.name)}`}
                        className="text-brand underline underline-offset-2"
                      >
                        {asset.deviceCount}
                      </Link>
                    ) : (
                      asset.deviceCount
                    )}
                  </Td>
                  {/* Offered only where temperature is what the asset is
                      judged on, for the same reason the form only asks for
                      those ranges (ADR-070): a tank has no temperature
                      record to produce. */}
                  <Td>
                    {monitors(asset.type, "TEMPERATURE") ? (
                      <Link
                        to={`/assets/${asset.id}/temperature-record`}
                        className="text-brand underline underline-offset-2"
                      >
                        Temperature
                      </Link>
                    ) : (
                      EM_DASH
                    )}
                  </Td>
                  {canCreate ? (
                    <Td>
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          aria-label={t("editAssetNamed", { name: asset.name })}
                          onClick={() => setEditing(asset)}
                        >
                          {t("editAsset")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          aria-label={t("deleteAssetNamed", { name: asset.name })}
                          onClick={() => setDeleting(asset)}
                        >
                          {t("deleteAsset")}
                        </Button>
                      </div>
                    </Td>
                  ) : null}
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <Td colSpan={columnCount} className="border-b-0">
                  <Pagination
                    page={assets.data.pagination.page}
                    totalPages={assets.data.pagination.totalPages}
                    total={assets.data.pagination.total}
                    noun={{ one: tCommon("noun.asset.one"), other: tCommon("noun.asset.other") }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      <AssetFormDialog open={creating} onClose={() => setCreating(false)} />

      {/* Mounted per asset so the form starts from that asset's values. */}
      {editing ? (
        <AssetFormDialog key={editing.id} open asset={editing} onClose={() => setEditing(null)} />
      ) : null}

      {deleting ? (
        <AssetDeleteDialog key={deleting.id} asset={deleting} onClose={() => setDeleting(null)} />
      ) : null}
    </>
  );
}
